import * as React from "react"

/** Matches Radix's own sub-menu delay, so every flyout opens at one speed. */
const HOVER_OPEN_DELAY_MS = 100

export interface UseHoverOpenOptions {
  /** Current open state of the flyout this trigger controls. */
  open: boolean
  /** Called with `true` once the pointer has rested on the trigger. */
  onOpenChange: (open: boolean) => void
  /** Rest time before opening. */
  delay?: number
  /** Set true to leave the trigger inert. */
  disabled?: boolean
}

export interface UseHoverOpenHandlers {
  onPointerEnter: (event: React.PointerEvent) => void
  onPointerLeave: () => void
}

/**
 * Reveals a flyout when the pointer rests on the parent item that owns it, the
 * way a native menu reveals a sub-menu. Spread the returned handlers onto the
 * parent item; keep its click handler, which is what keyboard and touch users
 * still open the flyout with.
 *
 * Hovering away deliberately does not close: the flyout is anchored beside the
 * item with a gap, so the pointer has to leave the item to reach it. Closing
 * stays with the click, the escape key, a click outside, and whichever sibling
 * item the pointer opens next — in a menu that keeps one section open at a
 * time, moving to a sibling swaps which flyout is showing.
 *
 * Touch and pen have no hover, so those pointers fall through to the click.
 */
export function useHoverOpen({
  open,
  onOpenChange,
  delay = HOVER_OPEN_DELAY_MS,
  disabled = false,
}: UseHoverOpenOptions): UseHoverOpenHandlers {
  const timerRef = React.useRef<number | null>(null)
  const openRef = React.useRef(open)

  React.useEffect(() => {
    openRef.current = open
  }, [open])

  const cancel = React.useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  React.useEffect(() => cancel, [cancel])

  const onPointerEnter = React.useCallback(
    (event: React.PointerEvent) => {
      if (disabled || open) {
        return
      }
      if (event.pointerType === "touch" || event.pointerType === "pen") {
        return
      }
      cancel()
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null
        /*
         * The pointer resting on an item is also the pointer about to click it,
         * so the flyout may already have been opened — by that click, or by a
         * keypress — while this timer ran. Asking again would land as a second
         * toggle and close what the click just opened.
         */
        if (openRef.current) {
          return
        }
        onOpenChange(true)
      }, delay)
    },
    [cancel, delay, disabled, onOpenChange, open],
  )

  return { onPointerEnter, onPointerLeave: cancel }
}
